import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRightOnRectangleIcon, UserCircleIcon, BellIcon, Bars3Icon } from '@heroicons/react/24/outline';

const routeLabels = {
  '/': 'Dashboard',
  '/admin': 'Admin Panel',
  '/call-monitor': 'Call Monitor',
};

const getPageTitle = (pathname) => {
  if (routeLabels[pathname]) return routeLabels[pathname];
  if (pathname.startsWith('/project/')) return 'Project';
  if (pathname.includes('cv-screening')) return 'CV Screening';
  if (pathname.includes('form-builder')) return 'Form Builder';
  if (pathname.includes('interviews')) return 'Interview Scheduling';
  if (pathname.includes('/reports/')) return 'HR Reports';
  if (pathname.includes('finance-reports')) return 'Financial Reports';
  return 'UNC AI';
};

const getInitials = (name) =>
  name ? name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) : 'U';

const AVATAR_COLORS = ['#4f46e5', '#16a34a', '#d97706', '#0891b2', '#dc2626'];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];

export default function Header({ onOpenMobile }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const pageTitle = getPageTitle(location.pathname);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header
      className="h-14 bg-white border-b border-slate-100 flex items-center justify-between px-6 flex-shrink-0"
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
    >
      <div className="flex items-center gap-2">
        <button
          onClick={onOpenMobile}
          className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-50 transition-colors"
          aria-label="Open menu"
        >
          <Bars3Icon className="w-5 h-5" />
        </button>
        <h2 className="text-sm font-semibold text-slate-800">{pageTitle}</h2>
      </div>

      <div className="flex items-center gap-1">
        <button className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors">
          <BellIcon className="w-5 h-5" />
        </button>

        <div className="relative ml-1">
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-2.5 pl-2.5 pr-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ backgroundColor: getAvatarColor(user?.full_name) }}
            >
              {getInitials(user?.full_name)}
            </div>
            <div className="text-left hidden sm:block">
              <p className="text-xs font-semibold text-slate-700 leading-tight">{user?.full_name}</p>
              <p className="text-[10px] text-slate-400 leading-tight capitalize">
                {user?.role}{user?.department_name ? ` · ${user.department_name}` : ''}
              </p>
            </div>
          </button>

          {open && (
            <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-slate-100 rounded-xl shadow-lg py-1.5 z-50 animate-scale-in">
              <div className="px-4 py-2.5 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-800">{user?.full_name}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{user?.email}</p>
              </div>
              <button
                onClick={() => { setOpen(false); navigate('/profile'); }}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-xs text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <UserCircleIcon className="w-3.5 h-3.5" /> My Profile
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-3.5 h-3.5" /> Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
